import { store } from 'redux/store';
import jwt_decode from 'jwt-decode';
import { JWTContent } from 'redux/reducers/authReducer';
import baseApi, { GenericResponse } from 'services/api/base';
import getProductKey from 'helper/requests/getProductKey';
import {
  transformSeasonalComparatorGetOptionsResponse,
  transformSeasonalComparatorListPreferencesResponse,
  transformSeasonalComparatorGetImages,
} from 'helper/Meteorology/SeasonalComparator';

import { SeasonalComparatorOptions } from 'types/Meteorology/Weather/SeasonalComparator/getSeasonalComparatorOptions';
import { SeasonalComparatorPreference } from 'types/Meteorology/Weather/SeasonalComparator/listPreferences';
import { GetImagesResponse, GetImagesParams, SeasonalComparatorImages } from 'types/Meteorology/Weather/SeasonalComparator/getImages';
import { SavePreferences } from 'types/Meteorology/Weather/SeasonalComparator/savePreferences';
import { DeletePreferenceParams } from 'types/Meteorology/Weather/SeasonalComparator/deletePreference';

export const seasonalComparatorApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    seasonalComparatorGetOptions: build.query<SeasonalComparatorOptions, void>({
      query: () => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/clima_exclusivo/comparador-sazonal/painel-options/?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformSeasonalComparatorGetOptionsResponse,
    }),
    seasonalComparatorGetImages: build.query<SeasonalComparatorImages, GetImagesParams>({
      query: ({
        variable, area, confrontingModel, baseModel,
      }) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/clima_exclusivo/comparador-sazonal/execute-comparison/?product_key=${productKey}`,
          method: 'POST',
          body: {
            variavel: variable,
            area,
            modelo_confrontante: {
              data_rodada: confrontingModel.roundDate,
              modelo: confrontingModel.model,
              ano_mes_inicial: confrontingModel.initialYearMonth,
              ano_mes_final: confrontingModel.finalYearMonth,
            },
            modelo_base: {
              data_rodada: baseModel.roundDate,
              modelo: baseModel.model,
              ano_mes_inicial: baseModel.initialYearMonth,
              ano_mes_final: baseModel.finalYearMonth,
            },
          },
        };
      },
      transformResponse: transformSeasonalComparatorGetImages,
    }),
    seasonalComparatorListPreferences: build.query<SeasonalComparatorPreference[], void>({
      query: () => {
        const productKey = getProductKey('meteorologia');
        const userId = jwt_decode<JWTContent>(store.getState().auth.jwt).user_id;
        return {
          url: `produtos/clima_exclusivo/comparador-sazonal/preferencias-list/?product_key=${productKey}`,
          method: 'POST',
          body: { user_id: userId },
        };
      },
      transformResponse: transformSeasonalComparatorListPreferencesResponse,
      providesTags: ['SeasonalComparatorPreferences'],
    }),
    seasonalComparatorSavePreference: build.mutation<GetImagesResponse, SavePreferences>({
      query: ({
        id, name, preferences,
      }) => {
        const userId = jwt_decode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('meteorologia');

        return {
          url: `produtos/clima_exclusivo/comparador-sazonal/save-preferences/?product_key=${productKey}`,
          method: 'POST',
          body: {
            id,
            user_id: userId,
            name,
            preferencias_json: {
              variavel: preferences.variable,
              area: preferences.area,
              modelo_confrontante: {
                data_rodada: preferences.confrontingModel.roundDate,
                modelo: preferences.confrontingModel.model,
                ano_mes_inicial: preferences.confrontingModel.initialYearMonth,
                ano_mes_final: preferences.confrontingModel.finalYearMonth,
              },
              modelo_base: {
                data_rodada: preferences.baseModel.roundDate,
                modelo: preferences.baseModel.model,
                ano_mes_inicial: preferences.baseModel.initialYearMonth,
                ano_mes_final: preferences.baseModel.finalYearMonth,
              },
            },
          },
        };
      },
      invalidatesTags: ['SeasonalComparatorPreferences'],
    }),
    seasonalComparatorDeletePreference: build.mutation<
    GenericResponse<null>,
    DeletePreferenceParams
    >({
      query: ({ id }) => {
        const productKey = getProductKey('meteorologia');
        const userId = jwt_decode<JWTContent>(store.getState().auth.jwt).user_id;
        return {
          url: `produtos/clima_exclusivo/comparador-sazonal/preferencias-delete/?product_key=${productKey}`,
          method: 'DELETE',
          body: { user_id: userId, id },
        };
      },
      invalidatesTags: ['SeasonalComparatorPreferences'],
    }),

  }),
});

export const {
  useSeasonalComparatorGetOptionsQuery,
  useLazySeasonalComparatorGetImagesQuery,
  useSeasonalComparatorListPreferencesQuery,
  useSeasonalComparatorSavePreferenceMutation,
  useSeasonalComparatorDeletePreferenceMutation,
} = seasonalComparatorApi;
